import {
  IVacationAward,
  IVacationAwardingCrew,
  IVacationAwardingResponse,
  IVacationAwardingSavePayload,
  IVacationAwardingSaveResponse,
  IVacationAwardingSlotUsage,
  IVacationAwardWarning,
  IVacationBidLeftoverPlacement,
  IVacationBidPackage,
  IVacationBidSlot,
  IVacationEntitlementOutcome,
  VacationAwardSource,
  VacationAwardWarningSource,
  VacationAwardingProgressStatus,
  VacationBidRound,
  VacationLeftoverDaySide,
  blockUnderLeftoverDays,
  leftoverDayRange,
} from "@vmosolutions/oc-interfaces";
import moment from "moment";
import { ISnackbarState } from "src/redux/reducers/SnackbarReducer";
import { compareVacationDates, hasSecondRound, roundBiddingHasClosed, vacationDateSpanDays } from "../helpers";

/**
 * The rounds the awarding tabs can work on, earliest first: the ones the package runs whose bidding
 * has closed. Round 2 opens on the same rule as round 1, and is where crew name their leftover days.
 */
export const awardableRounds = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  now = new Date()
): VacationBidRound[] =>
  [VacationBidRound.Round1, ...(hasSecondRound(pkg) ? [VacationBidRound.Round2] : [])].filter((round) =>
    roundBiddingHasClosed(pkg, round, now)
  );

/**
 * When a round's bidding next opens or closes after `now`, or null once none is still ahead. Rounds change on the
 * clock rather than on a render, so screens holding `awardableRounds` or `defaultViewRound` are woken then.
 */
export const nextRoundChange = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  now = new Date()
): Date | null => {
  const changes = [
    pkg?.bidWindowOpen,
    pkg?.bidWindowClose,
    ...(hasSecondRound(pkg) ? [pkg?.secondBidWindowOpen, pkg?.secondBidWindowClose] : []),
  ]
    .filter((z): z is Date => !!z)
    .map((z) => new Date(z).getTime())
    // An unparseable date gives NaN, which fails this as well and so is left out.
    .filter((z) => z > now.getTime())
    .sort((a, b) => a - b);
  return changes.length ? new Date(changes[0]) : null;
};

/** The round View Bids shows until the scheduler picks another: the latest whose bidding has opened. */
export const defaultViewRound = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  now = new Date()
): VacationBidRound =>
  hasSecondRound(pkg) && !!pkg?.secondBidWindowOpen && now >= new Date(pkg.secondBidWindowOpen)
    ? VacationBidRound.Round2
    : VacationBidRound.Round1;

/**
 * The round to award until the scheduler picks another: the earliest closed round still to publish, so round 1 is not
 * left behind once round 2 closes, else the latest closed one. Round 1 when none has closed, which the locked tab names.
 */
export const defaultAwardingRound = (
  rounds: VacationBidRound[],
  pkg?: Pick<IVacationBidPackage, "awardPublications"> | null
): VacationBidRound => {
  const published = new Set((pkg?.awardPublications ?? []).map((z) => z.round));
  return rounds.find((round) => !published.has(round)) ?? rounds[rounds.length - 1] ?? VacationBidRound.Round1;
};

/** A slot is half full once this share of its capacity has been awarded. */
export const HALF_FULL_RATIO = 0.5;

/** A slot is nearly full once this share of its capacity has been awarded. */
export const NEARLY_FULL_RATIO = 0.75;

export type FillBand = "open" | "halfFull" | "nearlyFull" | "full";

export const fillBand = (usage: Pick<IVacationAwardingSlotUsage, "awarded" | "capacity">): FillBand => {
  if (usage.awarded >= usage.capacity) return "full";
  const share = usage.awarded / usage.capacity;
  if (share >= NEARLY_FULL_RATIO) return "nearlyFull";
  if (share >= HALF_FULL_RATIO) return "halfFull";
  return "open";
};

/** Awarded past capacity, which is allowed with a warning. */
export const overCapacity = (usage: Pick<IVacationAwardingSlotUsage, "awarded" | "capacity">): number =>
  Math.max(0, usage.awarded - usage.capacity);

export interface IAwardingSummary {
  awarded: number;
  capacity: number;
  /** Open places across every slot. A slot awarded past its capacity adds nothing, and takes nothing from the others. */
  remaining: number;
  full: number;
  nearlyFull: number;
  crewComplete: number;
  crewTotal: number;
}

export const awardingSummary = (
  usage: IVacationAwardingSlotUsage[],
  crew: Pick<IVacationAwardingCrew, "progress">[]
): IAwardingSummary => ({
  awarded: usage.reduce((total, z) => total + z.awarded, 0),
  capacity: usage.reduce((total, z) => total + z.capacity, 0),
  remaining: usage.reduce((total, z) => total + Math.max(0, z.remaining), 0),
  full: usage.filter((z) => fillBand(z) === "full").length,
  nearlyFull: usage.filter((z) => fillBand(z) === "nearlyFull").length,
  crewComplete: crew.filter((z) => z.progress === VacationAwardingProgressStatus.Complete).length,
  crewTotal: crew.length,
});

export interface IQueueCounts {
  pending: number;
  skipped: number;
  done: number;
}

/** How many crew in the round are still to award, skipped for now, and marked complete. */
export const queueCounts = (crew: Pick<IVacationAwardingCrew, "progress">[]): IQueueCounts => {
  const skipped = crew.filter((z) => z.progress === VacationAwardingProgressStatus.Skipped).length;
  const done = crew.filter((z) => z.progress === VacationAwardingProgressStatus.Complete).length;
  return { pending: crew.length - skipped - done, skipped, done };
};

export type QueueState = "now" | "pending" | "skipped" | "done";

export const queueState = (
  crew: Pick<IVacationAwardingCrew, "userId" | "progress">,
  selectedUserId: string | null | undefined
): QueueState => {
  if (crew.userId === selectedUserId) return "now";
  if (crew.progress === VacationAwardingProgressStatus.Complete) return "done";
  if (crew.progress === VacationAwardingProgressStatus.Skipped) return "skipped";
  return "pending";
};

/**
 * Who to award next after `afterUserId`, going on down the seniority order and round to the top:
 * the next pending crew member, then the next skipped one. Null once everyone is complete.
 */
export const nextCrewToAward = (
  crew: Pick<IVacationAwardingCrew, "userId" | "progress">[],
  afterUserId?: string | null
): string | null => {
  const start = afterUserId ? crew.findIndex((z) => z.userId === afterUserId) : -1;
  const ordered = [...crew.slice(start + 1), ...crew.slice(0, start + 1)];
  return (
    ordered.find((z) => z.progress === null)?.userId ??
    ordered.find((z) => z.progress === VacationAwardingProgressStatus.Skipped)?.userId ??
    null
  );
};

export interface IEntitlementUse {
  totalDays: number;
  /** Null when the package's slots are not all one length, so no block count is meaningful. */
  blocks: number | null;
  /** Awards from every round count against the entitlement. */
  awarded: number;
  remaining: number | null;
  /**
   * How many blocks past the entitlement the crew member has been awarded. Null when there is no
   * block count to check against, which is not the same as not being over it.
   */
  over: number | null;
  leftoverDays: number | null;
}

export const entitlementUse = (crew: Pick<IVacationAwardingCrew, "entitlement" | "awards">): IEntitlementUse => {
  const { totalDays, blocks, leftoverDays } = crew.entitlement;
  const awarded = crew.awards.length;
  return {
    totalDays,
    blocks,
    awarded,
    remaining: blocks === null ? null : Math.max(0, blocks - awarded),
    over: blocks === null ? null : Math.max(0, awarded - blocks),
    leftoverDays,
  };
};

/** `Before` / `After`, for a column of its own. The enum's own value is the sentence form. */
export const sideLabel = (side: VacationLeftoverDaySide): string =>
  side === VacationLeftoverDaySide.Before ? "Before" : "After";

/** Leftover days as they sit on a block, with the block they hang off. */
export interface ILeftoverPlacement {
  slotId: string;
  side: VacationLeftoverDaySide;
  days: number;
}

/**
 * Where the crew member's leftover days sit now. Null until the scheduler has placed them.
 *
 * The days go on one block and are never split, so the first award carrying them is the only one.
 */
export const leftoverPlacement = (crew: Pick<IVacationAwardingCrew, "awards">): ILeftoverPlacement | null => {
  const holder = crew.awards.find((z) => z.leftoverDays);
  return holder?.leftoverDays ? { slotId: holder.slotId, ...holder.leftoverDays } : null;
};

/** One place the crew member asked for their leftover days, read against what they hold. */
export interface ILeftoverChoice extends IVacationBidLeftoverPlacement {
  /** They hold the block, so the days can go where they asked. */
  held: boolean;
  /** Their best choice they hold - what the scheduler follows unless they decide otherwise. */
  live: boolean;
  /** The days sit here now. */
  placed: boolean;
}

/**
 * What the crew member asked for, best first, against what they hold and where the days are.
 *
 * A block whose removal has been asked for and not yet landed does not count as held: the days
 * cannot be hung off one the server is about to take away.
 */
export const leftoverChoices = (
  crew: Pick<IVacationAwardingCrew, "awards" | "bid">,
  pendingRemoves: ReadonlySet<string> = new Set()
): ILeftoverChoice[] => {
  const held = new Set(crew.awards.map((z) => z.slotId).filter((slotId) => !pendingRemoves.has(slotId)));
  const placement = leftoverPlacement(crew);
  const ordered = [...(crew.bid?.leftoverDays?.placements ?? [])].sort((a, b) => a.rank - b.rank);
  const live = ordered.find((z) => held.has(z.slotId));
  return ordered.map((choice) => ({
    ...choice,
    held: held.has(choice.slotId),
    live: choice === live,
    placed: !!placement && choice.slotId === placement.slotId && choice.side === placement.side,
  }));
};

/** One block and side the scheduler can put the leftover days on. */
export interface ILeftoverTarget {
  slotId: string;
  side: VacationLeftoverDaySide;
  /** Where this sits in the crew member's own order, when they asked for it. */
  requestedRank: number | null;
  /** Another block they hold that the days would fall on, which the server refuses. */
  landsOn: string | null;
}

/**
 * Everywhere the days could go: each block the crew member holds, before it and after it, in date
 * order. A block no longer offered has no date to sort on, so it goes last, and one whose removal
 * is still being saved is not offered at all.
 */
export const leftoverTargets = (
  crew: Pick<IVacationAwardingCrew, "awards" | "bid" | "entitlement">,
  slotsById: Map<string, Pick<IVacationBidSlot, "slotId" | "startDate" | "endDate">>,
  pendingRemoves: ReadonlySet<string> = new Set()
): ILeftoverTarget[] => {
  const requested = new Map((crew.bid?.leftoverDays?.placements ?? []).map((z) => [`${z.slotId} ${z.side}`, z.rank]));
  const slotIds = [...new Set(crew.awards.map((z) => z.slotId))]
    .filter((slotId) => !pendingRemoves.has(slotId))
    .sort((a, b) => {
      const left = slotsById.get(a)?.startDate;
      const right = slotsById.get(b)?.startDate;
      if (!left || !right) return left ? -1 : right ? 1 : 0;
      return compareVacationDates(left, right);
    });
  const days = crew.entitlement.leftoverDays;
  const landsOn = (slotId: string, side: VacationLeftoverDaySide): string | null => {
    const slot = slotsById.get(slotId);
    if (!slot || !days) return null;
    const others = slotIds.filter((z) => z !== slotId).flatMap((z) => slotsById.get(z) ?? []);
    return blockUnderLeftoverDays(leftoverDayRange(slot, side, days), others)?.slotId ?? null;
  };
  return slotIds.flatMap((slotId) =>
    Object.values(VacationLeftoverDaySide).map((side) => ({
      slotId,
      side,
      requestedRank: requested.get(`${slotId} ${side}`) ?? null,
      landsOn: landsOn(slotId, side),
    }))
  );
};

/**
 * Whether a crew member holds all the vacation their entitlement allows, counting awards still being saved:
 * its whole blocks, or its days when the package's blocks are not all the same length.
 */
export const entitlementReached = (
  crew: Pick<IVacationAwardingCrew, "entitlement" | "awards">,
  slotsById: Map<string, Pick<IVacationBidSlot, "startDate" | "endDate">>,
  pendingSlotIds: Set<string>
): boolean => {
  const { blocks, totalDays } = crew.entitlement;
  if (blocks !== null) return crew.awards.length + pendingSlotIds.size >= blocks;
  const pendingDays = [...pendingSlotIds].reduce((total, slotId) => {
    const slot = slotsById.get(slotId);
    return total + (slot ? slotLengthDays(slot) : 0);
  }, 0);
  return awardedDays(crew) + pendingDays >= totalDays;
};

/** Whether a crew member holds fewer awards than the whole blocks their entitlement buys. */
export const crewIsShort = (crew: Pick<IVacationAwardingCrew, "entitlement" | "awards">): boolean =>
  (entitlementUse(crew).remaining ?? 0) > 0;

/** Where an award came from, in words. */
export const awardSourceLabel = (award: Pick<IVacationAward, "source" | "round" | "fromRank">): string => {
  if (award.source === VacationAwardSource.Assigned) return "Assigned";
  if (award.source === VacationAwardSource.ChangeRequest) return "Changed by request";
  return award.fromRank ? `Round ${award.round} bid, rank ${award.fromRank}` : `Round ${award.round} bid`;
};

/**
 * Whether the crew member already holds as many round 1 awards as the package allows, counting
 * awards still being saved, so the cap cannot be passed by clicking faster than the saves land.
 */
export const roundCapReached = (
  maxAwardsFirstRound: number | null | undefined,
  crew: Pick<IVacationAwardingCrew, "awards">,
  round: VacationBidRound,
  pendingAdds = 0
): boolean =>
  round === VacationBidRound.Round1 &&
  maxAwardsFirstRound !== null &&
  maxAwardsFirstRound !== undefined &&
  crew.awards.filter((z) => z.round === VacationBidRound.Round1).length + pendingAdds >= maxAwardsFirstRound;

/** An award, removal or short block move asked for and not yet landed. Always names its slot. */
export interface IPendingSlotChange {
  key: number;
  kind: "add" | "remove" | "shortBlock";
  userId: string;
  slotId: string;
}

/** A progress change asked for and not yet landed. About a crew member, not a slot. */
export interface IPendingProgressChange {
  key: number;
  kind: "progress";
  userId: string;
}

/**
 * A leftover-day placement asked for and not yet landed. About a crew member rather than a slot,
 * because it also covers taking the days off whichever block holds them.
 */
export interface IPendingLeftoverChange {
  key: number;
  kind: "leftover";
  userId: string;
}

/** One part of a save that has been asked for and not yet landed. */
export type IPendingChange = IPendingSlotChange | IPendingProgressChange | IPendingLeftoverChange;

export type PendingKind = IPendingChange["kind"];

/** The slots one crew member has a pending award, or removal, of. */
export const pendingSlotIds = (
  pending: IPendingChange[],
  userId: string | null,
  kind: IPendingSlotChange["kind"]
): Set<string> =>
  new Set(pending.filter((z): z is IPendingSlotChange => z.userId === userId && z.kind === kind).map((z) => z.slotId));

/** The parts of a save, as pending changes the screen can show until it lands. */
export const pendingChangesOf = (
  changes: Omit<IVacationAwardingSavePayload, "round">,
  nextKey: () => number
): IPendingChange[] => [
  ...(changes.add ?? []).map((z) => ({ key: nextKey(), kind: "add" as const, userId: z.userId, slotId: z.slotId })),
  ...(changes.remove ?? []).map((z) => ({
    key: nextKey(),
    kind: "remove" as const,
    userId: z.userId,
    slotId: z.slotId,
  })),
  ...(changes.progress ?? []).map((z) => ({ key: nextKey(), kind: "progress" as const, userId: z.userId })),
  ...(changes.leftover ?? []).map((z) => ({ key: nextKey(), kind: "leftover" as const, userId: z.userId })),
  ...(changes.shortBlock ?? []).map((z) => ({
    key: nextKey(),
    kind: "shortBlock" as const,
    userId: z.userId,
    slotId: z.slotId,
  })),
];

/**
 * Runs tasks one at a time, in the order they were queued, whether or not earlier ones failed. A
 * task queued while another runs waits its turn instead of being refused.
 */
export const createSerialQueue = (): (<T>(task: () => Promise<T>) => Promise<T>) => {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const result = tail.then(task);
    tail = result.then(
      (): undefined => undefined,
      (): undefined => undefined
    );
    return result;
  };
};

/** Round 2 has opened to crew while round 1's awards are still unreleased. */
export const round2OpenedBeforePublishing = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  view: Pick<IVacationAwardingResponse, "round" | "publication">,
  now = new Date()
): boolean =>
  view.round === VacationBidRound.Round1 &&
  !view.publication &&
  hasSecondRound(pkg) &&
  now >= new Date(pkg?.secondBidWindowOpen as Date | string);

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export interface ISlotMonth {
  key: string;
  /** The month, with its year only when the slots run across more than one. */
  label: string;
  slots: IVacationBidSlot[];
}

/** Slots grouped by the month they start in, keeping the order they arrive in. */
export const slotsByMonth = (slots: IVacationBidSlot[]): ISlotMonth[] => {
  const spansYears = new Set(slots.map((z) => z.startDate.year)).size > 1;
  const months: ISlotMonth[] = [];
  for (const slot of slots) {
    const { year, month } = slot.startDate;
    const key = `${year}-${month}`;
    let group = months.find((z) => z.key === key);
    if (!group) {
      group = { key, label: spansYears ? `${MONTHS[month - 1]} ${year}` : MONTHS[month - 1], slots: [] };
      months.push(group);
    }
    group.slots.push(slot);
  }
  return months;
};

export const crewName = (crew: Pick<IVacationAwardingCrew, "firstName" | "lastName" | "employeeId">): string =>
  `${crew.firstName ?? ""} ${crew.lastName ?? ""}`.trim() || crew.employeeId || "Unnamed crew member";

/** Whether a crew member's name or employee id contains the search, ignoring case. A blank search matches everyone. */
export const crewMatchesSearch = (
  crew: Pick<IVacationAwardingCrew, "firstName" | "lastName" | "employeeId">,
  search: string
): boolean => {
  const query = search.trim().toLowerCase();
  return !query || [crewName(crew), crew.employeeId ?? ""].some((text) => text.toLowerCase().includes(query));
};

/** Who holds each slot, by name, most senior first. */
export const crewNamesBySlot = (crew: IVacationAwardingCrew[]): Map<string, string[]> => {
  const names = new Map<string, string[]>();
  for (const member of crew) {
    for (const award of member.awards) {
      names.set(award.slotId, [...(names.get(award.slotId) ?? []), crewName(member)]);
    }
  }
  return names;
};

const vacationDay = (date: IVacationBidSlot["startDate"]): moment.Moment =>
  moment.utc({ year: date.year, month: date.month - 1, day: date.day });

/** `Jul 05 - Jul 09`. */
export const slotDateRange = (slot: Pick<IVacationBidSlot, "startDate" | "endDate">): string =>
  `${vacationDay(slot.startDate).format("MMM DD")} - ${vacationDay(slot.endDate).format("MMM DD")}`;

export const slotLengthDays = (slot: Pick<IVacationBidSlot, "startDate" | "endDate">): number =>
  vacationDateSpanDays(slot.startDate, slot.endDate);

/** `3 of 5 days` for a short block, which only covers the slot's first days. Null for a full one. */
export const shortBlockLabel = (
  award: Pick<IVacationAward, "blockDays">,
  slot: Pick<IVacationBidSlot, "startDate" | "endDate">
): string | null => {
  const length = slotLengthDays(slot);
  return award.blockDays < length ? `${award.blockDays} of ${length} days` : null;
};

/** The days an award covers, from its slot's first: `Mar 08 - Mar 10` for a 3-day short block. */
export const awardDateRange = (
  award: Pick<IVacationAward, "blockDays">,
  slot: Pick<IVacationBidSlot, "startDate" | "endDate">
): string => {
  const start = vacationDay(slot.startDate);
  const end = start.clone().add(Math.min(award.blockDays, slotLengthDays(slot)) - 1, "days");
  return `${start.format("MMM DD")} - ${end.format("MMM DD")}`;
};

/**
 * The award covering fewer days than its slot: the crew member's short block. When several are short it is the one
 * made last, then the later block, as the server takes it.
 */
export const shortBlockOf = (
  crew: Pick<IVacationAwardingCrew, "awards">,
  slotsById: Map<string, Pick<IVacationBidSlot, "startDate" | "endDate">>
): IVacationAward | null => {
  const madeAt = (award: IVacationAward): number => (award.createdAt ? new Date(award.createdAt).valueOf() : 0);
  const startOf = (award: IVacationAward): number => {
    const slot = slotsById.get(award.slotId);
    return slot ? vacationDay(slot.startDate).valueOf() : 0;
  };
  return crew.awards
    .filter((award) => {
      const slot = slotsById.get(award.slotId);
      return !!slot && award.blockDays < slotLengthDays(slot);
    })
    .reduce<IVacationAward | null>(
      (latest, award) =>
        !latest ||
        (madeAt(award) !== madeAt(latest) ? madeAt(award) > madeAt(latest) : startOf(award) > startOf(latest))
          ? award
          : latest,
      null
    );
};

/** What one award offers for the short block: it is the short block, or a button to make it the short block. */
export type ShortBlockAction = { kind: "short"; label: string } | { kind: "makeShort"; disabledReason: string };

/**
 * Whether an award is the crew member's short block, or can be made it, with the server's reasons for refusing
 * so the button says why first. Null when nothing is short and their entitlement leaves no part block to make short.
 */
export const shortBlockAction = (
  crew: Pick<IVacationAwardingCrew, "awards" | "entitlement">,
  slotId: string,
  slotsById: Map<string, Pick<IVacationBidSlot, "startDate" | "endDate">>,
  { writesAllowed, saving }: { writesAllowed: boolean; saving: boolean }
): ShortBlockAction | null => {
  const award = crew.awards.find((z) => z.slotId === slotId);
  const slot = slotsById.get(slotId);
  if (!award || !slot) return null;
  const short = shortBlockOf(crew, slotsById);
  if (short?.slotId === slotId) return { kind: "short", label: shortBlockLabel(award, slot) ?? "" };
  if (!short && crew.entitlement.shortBlockDays === null) return null;

  const shortSlot = short ? slotsById.get(short.slotId) : undefined;
  const disabledReason = award.leftoverDays?.days
    ? "It carries their leftover days, so it cannot be their short block."
    : shortSlot && slotLengthDays(shortSlot) !== slotLengthDays(slot)
      ? `It is a ${slotLengthDays(slot)}-day block and their short block is in a ${slotLengthDays(shortSlot)}-day one, so it cannot be their short block.`
      : !writesAllowed
        ? "This round can no longer be changed"
        : saving
          ? "Saving…"
          : "";
  return { kind: "makeShort", disabledReason };
};

/** Days of vacation held, short blocks counted by their own days. */
export const awardedDays = (crew: Pick<IVacationAwardingCrew, "awards">): number =>
  crew.awards.reduce((total, award) => total + award.blockDays, 0);

/** A view with a save's results folded in: fresh usage, and the touched crew's awards and progress. */
export const applySaveResponse = (
  view: IVacationAwardingResponse,
  response: IVacationAwardingSaveResponse
): IVacationAwardingResponse => {
  const updates = new Map(response.crew.map((z) => [z.userId, z]));
  return {
    ...view,
    usage: response.usage,
    crew: view.crew.map((member) => {
      const update = updates.get(member.userId);
      return update ? { ...member, progress: update.progress, awards: update.awards } : member;
    }),
  };
};

/** Warnings from entitlement changes still open on a crew member, oldest first. */
export const openWarnings = (crew: Pick<IVacationAwardingCrew, "warnings">): IVacationAwardWarning[] =>
  crew.warnings ?? [];

/** Whether hiding done crew hides this one. A crew member with a warning still open stays listed until it is dismissed. */
export const hiddenWhenDone = (
  crew: Pick<IVacationAwardingCrew, "userId" | "progress" | "warnings">,
  selectedUserId: string | null | undefined
): boolean =>
  crew.progress === VacationAwardingProgressStatus.Complete &&
  crew.userId !== selectedUserId &&
  openWarnings(crew).length === 0;

/** What changed a crew member's entitlement, and when, as a warning's heading. */
export const warningTitle = (warning: IVacationAwardWarning): string => {
  const when = moment(warning.createdAt).format("MMM DD, YYYY HH:mm");
  const days = warning.fromDays === null ? `${warning.toDays}` : `from ${warning.fromDays} to ${warning.toDays}`;
  if (warning.source === VacationAwardWarningSource.SlotSave) {
    return `On ${when} a change to the package's blocks changed how their ${warning.toDays} days divide.`;
  }
  const where = warning.source === VacationAwardWarningSource.EntitlementImport ? "a CSV import" : "Add Crew";
  return `On ${when} their days went ${days} in ${where}.`;
};

/** A warning's body: what was done to their awards, then what is left for the scheduler. */
export const warningLines = (warning: Pick<IVacationAwardWarning, "messages" | "actions">): string[] => [
  ...warning.messages,
  ...warning.actions,
  ...(warning.actions.length ? ["Please take the necessary action."] : []),
];

/** Names shown in a list before the rest are counted instead. */
const NAMES_LISTED = 5;

const plural = (count: number, one: string, many = `${one}s`): string => `${count} ${count === 1 ? one : many}`;

/** The first few names, and how many more there are. */
const listedNames = (crew: Pick<IVacationAwardingCrew, "firstName" | "lastName" | "employeeId">[]): string => {
  const names = crew.slice(0, NAMES_LISTED).map(crewName).join(", ");
  return crew.length > NAMES_LISTED ? `${names} and ${crew.length - NAMES_LISTED} more` : names;
};

/** Why the round cannot be published yet: crew not marked complete. Null once everyone is. */
export const publishBlockedReason = (
  crew: Pick<IVacationAwardingCrew, "progress" | "firstName" | "lastName" | "employeeId">[]
): string | null => {
  const unfinished = crew.filter((member) => member.progress !== VacationAwardingProgressStatus.Complete);
  if (!unfinished.length) return null;
  return (
    `${plural(unfinished.length, "crew member")} ${unfinished.length === 1 ? "is" : "are"} not marked complete: ` +
    `${listedNames(unfinished)}. Mark everyone complete to publish.`
  );
};

/** What publishing the round will do, one statement per line, for the confirm before it happens. */
export const publishSummaryLines = (
  view: Pick<IVacationAwardingResponse, "round" | "crew">,
  isLastRound: boolean
): string[] => {
  const { round, crew } = view;
  const roundAwards = crew.flatMap((member) => member.awards.filter((z) => z.round === round));
  const awardedCrew = crew.filter((member) => member.awards.some((z) => z.round === round)).length;

  const lines = [
    `This releases ${plural(roundAwards.length, "round " + String(round) + " award")} for ${plural(awardedCrew, "crew member")} to crew.`,
    `Round ${round} then becomes read-only, and it cannot be unpublished.`,
  ];

  // Leftover days are placed in the last round's awarding; after its publish only a change request can.
  const unplaced = crew.filter(
    (member) =>
      isLastRound &&
      !!member.entitlement.leftoverDays &&
      // Only where the days could actually have gone somewhere. A crew member holding no blocks at
      // all has a bigger problem than their leftover days, and the line would be noise.
      member.awards.length > 0 &&
      !member.awards.some((award) => award.leftoverDays)
  );
  if (unplaced.length) {
    lines.push(
      `${plural(unplaced.length, "crew member")} still ${unplaced.length === 1 ? "has" : "have"} ` +
        `leftover days with nowhere to go: ${listedNames(unplaced)}.`
    );
  }

  // Listed rather than blocking: some can only be fixed outside the system once every round is published.
  const warned = crew.filter((member) => openWarnings(member).length > 0);
  if (warned.length) {
    lines.push(
      `${plural(warned.length, "crew member")} still ${warned.length === 1 ? "has" : "have"} ` +
        `entitlement change warnings open: ${listedNames(warned)}.`
    );
  }
  return lines;
};

/**
 * The message after saving a crew member's days: that they saved, then what that did to their awards. Given a name,
 * it says whose, for a save that lands after the scheduler has left Add Crew.
 */
export const entitlementSavedLines = (
  outcome: Pick<IVacationEntitlementOutcome, "messages" | "actions"> | null | undefined,
  name?: string
): string[] => [
  name ? `${name}'s entitlement was updated.` : "Entitlement updated.",
  ...(outcome ? warningLines(outcome) : []),
];

/** Days left unsaved because the scheduler left Add Crew before choosing what to do with the awards they change. */
export const entitlementNotSavedSnackbar = (name: string): ISnackbarState => ({
  open: true,
  closeDelay: null,
  variant: "error",
  message: `${name}'s entitlement was not saved, as you left Add Crew before choosing what to do with their awards. Enter it again to save it.`,
});

/** What an import or slot save did to crew members' awards. Null when it changed none and flagged none. */
export const awardAdjustmentsLine = (
  counts: { adjusted?: number; flagged?: number } | null | undefined
): string | null => {
  const adjusted = counts?.adjusted ?? 0;
  const flagged = counts?.flagged ?? 0;
  if (!adjusted && !flagged) return null;
  return (
    `${plural(adjusted, "crew member")} had their awards adjusted to match, and ` +
    `${plural(flagged, "crew member")} ${flagged === 1 ? "has" : "have"} a warning in Award Bids.`
  );
};

/** A message after a save that may have changed awards. It stays open while something is left for the scheduler. */
export const awardChangesSnackbar = (lines: string[], needsAction: boolean): ISnackbarState => ({
  open: true,
  closeDelay: needsAction ? null : 3000,
  variant: needsAction ? "warning" : "success",
  message: lines.join(" "),
});
